import { useCreateConnector } from './use-create-connector';
import { useImportProviderFromManifest } from '../providers/use-add-provider-from-manifest';
import type { Connector } from '@/types/core/connector';
import type { Provider } from '@/types/core/provider';
import type { ProviderManifest } from '@/types/core/provider-manifest';
import type { UUID } from '@/utils/uuid';

export type AddConnectorsResult = {
  /** Number of connectors successfully created. */
  created: number;
  /** Number of provider imports that failed before connector creation. */
  failedImports: number;
  /** Display name of the first template — used for single-item toast messages. */
  firstName: string | undefined;
  /**
   * ID of the first successfully created connector — used to jump straight
   * into configuration when only a single connector was added.
   */
  firstConnectorId: string | undefined;
};

/**
 * Orchestrates the multi-step "add connectors" flow:
 * 1. Skip import for templates whose provider is already installed.
 * 2. Import providers that aren't installed yet.
 * 3. Auto-create a connector for each successfully resolved provider.
 *
 * Returns a result object that the caller uses to decide on feedback (toasts,
 * error messages, etc.) — this hook does not produce any UI side-effects itself.
 */
export function useAddConnectors(tenantId?: UUID) {
  const importMutation = useImportProviderFromManifest(tenantId);
  const createConnectorMutation = useCreateConnector(tenantId);

  const isPending =
    importMutation.isPending || createConnectorMutation.isPending;

  const addConnectors = async (
    templates: ProviderManifest[],
    userProviders: Provider[],
    existingConnectors: Connector[]
  ): Promise<AddConnectorsResult> => {
    const installedProviderMap = new Map(userProviders.map((p) => [p.id, p]));

    const alreadyInstalled = templates
      .filter((t) => t.providerId && installedProviderMap.has(t.providerId))
      .map((t) => installedProviderMap.get(t.providerId!)!);

    const needsImport = templates.filter(
      (t) => !t.providerId || !installedProviderMap.has(t.providerId)
    );

    const importResults = await Promise.allSettled(
      needsImport.map((t) => importMutation.mutateAsync(t))
    );

    const importedProviders = importResults
      .filter(
        (r): r is PromiseFulfilledResult<Provider> => r.status === 'fulfilled'
      )
      .map((r) => r.value);

    const allProviders = [...alreadyInstalled, ...importedProviders];
    const failedImports = needsImport.length - importedProviders.length;

    if (allProviders.length === 0) {
      return {
        created: 0,
        failedImports,
        firstName: templates[0]?.displayName ?? templates[0]?.name,
        firstConnectorId: undefined,
      };
    }

    const connectorCountsByProviderId = new Map<UUID, number>();
    existingConnectors.forEach((connector) => {
      const nextCount =
        (connectorCountsByProviderId.get(connector.providerId) ?? 0) + 1;
      connectorCountsByProviderId.set(connector.providerId, nextCount);
    });

    const connectorResults = await Promise.allSettled(
      allProviders.map((p) => {
        const nextCount = (connectorCountsByProviderId.get(p.id) ?? 0) + 1;
        connectorCountsByProviderId.set(p.id, nextCount);
        const baseName = `${p.name ?? 'New'} Connector`;

        return createConnectorMutation.mutateAsync({
          name: nextCount === 1 ? baseName : `${baseName} ${nextCount}`,
          providerId: p.id,
        });
      })
    );

    const createdConnectors = connectorResults
      .filter(
        (r): r is PromiseFulfilledResult<Connector> => r.status === 'fulfilled'
      )
      .map((r) => r.value);

    return {
      created: createdConnectors.length,
      failedImports,
      firstName: templates[0]?.displayName ?? templates[0]?.name,
      firstConnectorId: createdConnectors[0]?.id,
    };
  };

  return { addConnectors, isPending };
}
